import { t } from 'i18n';
import React from 'react';
import { ContainerLoading } from 'components/Loading';
import { useAutogenSelectedProduct, useDynamicSelectedProduct } from 'hooks';
import { ProductVariantsMobile } from '@teko/ecommerce/mobile';
import {
  Label,
  DefaultPromo,
  DefaultPromoContent,
  Divider,
} from 'components/ProductDetail/styles';
import { PromotionOption } from 'components/desktop/organisms';
import { convertGift, convertCommonPromotion } from 'helpers';
import { ComboDetailPayload } from '@teko/discovery';
import { ComboProduct } from 'types';
import {
  Modal,
  styled,
  Quantity,
  Caption,
  Button,
  SubTitle,
  Card,
} from '@teko/ds-desktop';
import { BlockHeader, DefaultPromotions } from '@teko/teko-ecommerce';
interface ChangeAutogenProductPropModalProps {
  visible: boolean;
  onCloseModal: () => any;
  product: ComboProduct;
  index: number;
  onChangeAutogenProduct: (index: number, newProduct: ComboProduct) => void;
}
export const ChangeAutogenProductPropModal: React.FC<
  ChangeAutogenProductPropModalProps
> = ({
  visible,
  onCloseModal,
  product: prod,
  index,
  onChangeAutogenProduct,
}: ChangeAutogenProductPropModalProps) => {
  const {
    product,
    loading,
    onChangeQuantity,
    onChangeSelectionPromotionId,
    onChangeVariant,
    onFinish,
    selectionPromotionId,
  } = useAutogenSelectedProduct(index, prod);
  return (
    <Modal show={visible} onHide={onCloseModal}>
      <Card>
        <BlockHeader title={t('Tùy chọn sản phẩm')} />
        <ContainerLoading isLoading={loading}>
          <WrapperBodyModal>
            {product?.productConfigurations.map((config, index) => (
              <ProductVariantsMobile
                key={config.id}
                configuration={config}
                index={index}
                onSelectVariant={onChangeVariant}
              />
            ))}
            <WrapperQuantity>
              <Caption level={3} color="#848788" marginBottom="s8">
                {t('Số lượng')}
              </Caption>
              <Quantity
                setQuantity={(val) => onChangeQuantity(val)}
                quantity={product?.quantity}
                min={1}
                max={product?.totalAvailable}
              />
              {product?.totalAvailable && product?.totalAvailable < 5 && (
                <Caption level={1} color="#E6983A" marginTop="s8">
                  {t('Chỉ còn {{productTotalAvailable}} sản phẩm', {
                    productTotalAvailable: product?.totalAvailable,
                  })}
                </Caption>
              )}
            </WrapperQuantity>
            {(!!product?.defaultPromotions?.length ||
              product?.saveWithoutSelectionPromotion > 0) && (
              <DefaultPromo>
                <Label>{t('Bạn sẽ nhận được')}</Label>
                <DefaultPromoContent>
                  <DefaultPromotions
                    gifts={product?.gifts?.map(convertGift)}
                    discount={product?.saveWithoutSelectionPromotion}
                    vouchers={product?.vouchers}
                  />
                </DefaultPromoContent>
              </DefaultPromo>
            )}
            {!!product?.selectionPromotions?.length && (
              <>
                <Divider />
                <Label>{t('Chọn thêm 1 trong những khuyến mãi sau')}</Label>
                {product?.selectionPromotions.map((promo, index: number) => {
                  const { promotion: convertedPromotion } =
                    convertCommonPromotion(
                      promo,
                      product?.priceWithoutSelectionPromotion,
                    );
                  return (
                    <PromotionOption
                      promotion={convertedPromotion}
                      key={index}
                      active={promo.id === selectionPromotionId}
                      onClick={() =>
                        onChangeSelectionPromotionId(
                          promo.id === selectionPromotionId
                            ? undefined
                            : promo.id,
                        )
                      }
                    />
                  );
                })}
              </>
            )}
          </WrapperBodyModal>
          <WrapperButtonGroup>
            <Button
              onClick={onCloseModal}
              style={{ paddingLeft: '3rem', paddingRight: '3rem' }}
            >
              <SubTitle level={2} weight="medium">
                {t('Bỏ qua')}
              </SubTitle>
            </Button>
            <Button
              onClick={() => {
                onFinish(onChangeAutogenProduct);
                onCloseModal();
              }}
              marginLeft="s8"
              variant="primary"
              paddingLeft="s32"
              paddingRight="s32"
            >
              <SubTitle level={2} weight="medium">
                {t('Hoàn tất')}
              </SubTitle>
            </Button>
          </WrapperButtonGroup>
        </ContainerLoading>
      </Card>
    </Modal>
  );
};
interface ChangeDynamicProductPropModalProps {
  visible: boolean;
  onCloseModal: () => any;
  payload: ComboDetailPayload;
  restSkus: string[];
  onChangeDynamicProduct: (newPayload: ComboDetailPayload) => void;
}
export const ChangeDynamicProductPropModal: React.FC<
  ChangeDynamicProductPropModalProps
> = ({
  visible,
  onCloseModal,
  payload,
  restSkus,
  onChangeDynamicProduct,
}: ChangeDynamicProductPropModalProps) => {
  const { product, loading, onChangeVariant, onFinish } =
    useDynamicSelectedProduct(restSkus, payload);
  return (
    <Modal show={visible} onHide={onCloseModal}>
      <Card>
        <BlockHeader title={t('Tùy chọn sản phẩm')} />
        <ContainerLoading isLoading={loading}>
          <WrapperBodyModal>
            {product?.productConfigurations.map((config, index) => (
              <ProductVariantsMobile
                key={config.id}
                configuration={config}
                index={index}
                onSelectVariant={onChangeVariant}
              />
            ))}
            <WrapperQuantity>
              <Caption level={3} color="#848788" marginBottom="s8">
                {t('Số lượng')}
              </Caption>
              <Quantity
                disabled
                setQuantity={() => {}}
                quantity={product?.quantity}
                min={1}
                max={product?.totalAvailable}
              />
              {product?.totalAvailable && product?.totalAvailable < 5 && (
                <Caption level={1} color="#E6983A" marginTop="s8">
                  {t('Chỉ còn {{totalAvailable}} sản phẩm', {
                    totalAvailable: product?.totalAvailable,
                  })}
                </Caption>
              )}
            </WrapperQuantity>
            {(!!product?.defaultPromotions?.length ||
              product?.saveWithoutSelectionPromotion > 0) && (
              <DefaultPromo>
                <Label>{t('Bạn sẽ nhận được')}</Label>
                <DefaultPromoContent>
                  <DefaultPromotions
                    gifts={product?.gifts?.map(convertGift)}
                    discount={product?.saveWithoutSelectionPromotion}
                    vouchers={product?.vouchers}
                  />
                </DefaultPromoContent>
              </DefaultPromo>
            )}
          </WrapperBodyModal>
          <WrapperButtonGroup>
            <Button
              onClick={onCloseModal}
              style={{ paddingLeft: '3rem', paddingRight: '3rem' }}
            >
              <SubTitle level={2} weight="medium">
                {t('Bỏ qua')}
              </SubTitle>
            </Button>
            <Button
              onClick={() => {
                onFinish(onChangeDynamicProduct);
                onCloseModal();
              }}
              marginLeft="s8"
              variant="primary"
              paddingLeft="s32"
              paddingRight="s32"
            >
              <SubTitle level={2} weight="medium">
                {t('Hoàn tất')}
              </SubTitle>
            </Button>
          </WrapperButtonGroup>
        </ContainerLoading>
      </Card>
    </Modal>
  );
};
const WrapperBodyModal = styled.div`
  position: relative;
  padding: 1.5rem;
  max-height: 35rem;
  overflow: auto;
  border-bottom: 1px dashed rgb(221, 221, 221);
  border-top: 1px dashed rgb(221, 221, 221);
  ::-webkit-scrollbar-track {
    -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
    background-color: #f5f5f5;
  }

  ::-webkit-scrollbar {
    width: 8px;
    background-color: #f5f5f5;
  }

  ::-webkit-scrollbar-thumb {
    background-color: #999;
  }
`;
const WrapperButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 12px;
`;
const WrapperQuantity = styled.div`
  margin-top: 8px;
`;
